import type {
  AtlasCategory,
  AtlasCompetenceDonnee,
  AtlasDonnee,
  AtlasSousDomaine,
  AtlasTypeOuvrage,
} from '@/models/interfaces/map/AtlasCategory'
import type { TypeOuvrageMarkerConfig } from '@/models/interfaces/map/MarkerConfig'
import { apiClient } from '@/plugins/axios/api'

/**
 * Service to fetch and cache atlas categories from the backend API.
 */
export class AtlasCategoriesService {
  private static readonly DONNEE_STYLE_PREFIX = 'atlas_donnee_'
  private static readonly NATIONAL_SCOPE_KEY = '__national__'
  /** Cached categories keyed by scope: national (no commune) or a commune's cmnCode. */
  private static categoriesCacheByScope: globalThis.Map<string, AtlasCategory[]> = new globalThis.Map()
  private static categoriesPromiseByScope: globalThis.Map<string, Promise<AtlasCategory[]>> = new globalThis.Map()

  private static scopeKey(cmnCode?: string | null): string {
    return cmnCode ? cmnCode : this.NATIONAL_SCOPE_KEY
  }

  private static toDonneeStyleId(code: string, fallbackId: number | string): string {
    const normalizedCode = code.trim().replace(/\s+/g, '_').toLowerCase()
    if (normalizedCode.length > 0) return `${this.DONNEE_STYLE_PREFIX}${normalizedCode}`
    return `${this.DONNEE_STYLE_PREFIX}${String(fallbackId).trim().toLowerCase()}`
  }

  /**
   * Reads marker_config off a raw API row, accepting both casings (markerConfig / marker_config —
   * the admin type_ouvrages endpoint is known to use snake_case; atlas_categories wasn't checked
   * for both until this was found to silently drop the field for every type d'ouvrage row).
   */
  private static extractMarkerConfig(row: Record<string, unknown>): Record<string, unknown> | undefined {
    const value = row.markerConfig ?? row.marker_config
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined
  }

  private static normalizeDonnee(raw: unknown, fallbackIndex: number): AtlasDonnee | null {
    if (!raw || typeof raw !== 'object') return null
    const row = raw as Record<string, unknown>
    const explicitId = row.id
    const typeOuvrageCode = typeof row.typeOuvrageCode === 'string'
      ? row.typeOuvrageCode
      : typeof row.type_ouvrage_code === 'string'
        ? row.type_ouvrage_code
        : undefined
    const id =
      typeof explicitId === 'number' || typeof explicitId === 'string'
        ? explicitId
        : typeOuvrageCode ?? fallbackIndex
    const typeOuvrageLib = typeof row.typeOuvrageLib === 'string'
      ? row.typeOuvrageLib
      : typeof row.type_ouvrage_lib === 'string'
        ? row.type_ouvrage_lib
        : undefined
    const fallbackName = String(
      typeOuvrageLib ?? row.nameFr ?? row.nameEn ?? row.name ?? row.label ?? row.code ?? `Donnee ${id}`,
    )
    const order = typeof row.order === 'number' ? row.order : (typeof row.position === 'number' ? row.position : fallbackIndex)
    const styleIdValue =
      row.styleId ?? row.style_id ?? row.mapStyleId ?? row.map_style_id ?? row.osmStyleId ?? row.osm_style_id
    const styleId =
      typeof styleIdValue === 'string' && styleIdValue.trim().length > 0
        ? styleIdValue
        : this.toDonneeStyleId(typeOuvrageCode ?? '', id)
    const count = typeof row.count === 'number' ? row.count : undefined
    const hasData = typeof row.hasData === 'boolean' ? row.hasData : (typeof count === 'number' ? count > 0 : undefined)
    const code = typeof row.code === 'string' ? row.code : typeOuvrageCode

    return {
      id,
      typeOuvrageCode,
      typeOuvrageLib,
      markerConfig: this.extractMarkerConfig(row),
      source: typeof row.source === 'string' ? row.source : undefined,
      count,
      code,
      name: fallbackName,
      nameFr: typeof row.nameFr === 'string' ? row.nameFr : fallbackName,
      nameEn: typeof row.nameEn === 'string' ? row.nameEn : fallbackName,
      description: typeof row.description === 'string' ? row.description : undefined,
      order,
      tableName: typeof row.tableName === 'string' ? row.tableName : undefined,
      geometryType:
        row.geometryType === 'point' ||
        row.geometryType === 'line' ||
        row.geometryType === 'polygon' ||
        row.geometryType === 'roads'
          ? row.geometryType
          : undefined,
      defaultStyle:
        row.defaultStyle && typeof row.defaultStyle === 'object'
          ? (row.defaultStyle as AtlasDonnee['defaultStyle'])
          : undefined,
      hasData,
      icon: typeof row.icon === 'string' || row.icon === null ? row.icon : undefined,
      styleId,
    }
  }

  private static isNewFormatDonnee(raw: unknown): boolean {
    if (!raw || typeof raw !== 'object') return false
    return Array.isArray((raw as Record<string, unknown>).typeOuvrages)
  }

  private static normalizeTypeOuvrage(raw: unknown, fallbackIndex: number): AtlasTypeOuvrage | null {
    if (!raw || typeof raw !== 'object') return null
    const r = raw as Record<string, unknown>
    const typId = typeof r.typId === 'number' ? r.typId : fallbackIndex
    const typeOuvrageCode = typeof r.typeOuvrageCode === 'string' ? r.typeOuvrageCode : `TYPE_${typId}`
    const typeOuvrageLib = typeof r.typeOuvrageLib === 'string' ? r.typeOuvrageLib : typeOuvrageCode
    // Libellé anglais facultatif : gardé tel quel, y compris absent, pour que libelleDynamique
    // puisse retomber sur le français plutôt que d'afficher le code du type.
    const typeOuvrageLibEn = typeof r.typeOuvrageLibEn === 'string' ? r.typeOuvrageLibEn : undefined
    const count = typeof r.count === 'number' ? r.count : 0
    const styleId =
      typeof r.styleId === 'string' && r.styleId.trim().length > 0
        ? r.styleId
        : `infra_type_ouvrage_${typId}`
    return {
      typId,
      typeOuvrageCode,
      typeOuvrageLib,
      typeOuvrageLibEn,
      markerConfig: this.extractMarkerConfig(r) ?? null,
      count,
      styleId,
    }
  }

  private static normalizeCompetenceDonnee(
    raw: unknown,
    fallbackIndex: number,
  ): AtlasCompetenceDonnee | null {
    if (!raw || typeof raw !== 'object') return null
    const r = raw as Record<string, unknown>
    if (!Array.isArray(r.typeOuvrages)) return null
    const competenceId = typeof r.competenceId === 'number' ? r.competenceId : fallbackIndex
    const cptCode = typeof r.cptCode === 'string' ? r.cptCode : `CPT_${competenceId}`
    const cptLib = typeof r.cptLib === 'string' ? r.cptLib : cptCode
    const cptLibEn = typeof r.cptLibEn === 'string' ? r.cptLibEn : undefined
    const typeOuvrages = (r.typeOuvrages as unknown[])
      .map((t, i) => this.normalizeTypeOuvrage(t, i))
      .filter((t): t is AtlasTypeOuvrage => t != null)
    return { competenceId, cptCode, cptLib, cptLibEn, typeOuvrages }
  }

  private static normalizeSousDomaine(raw: unknown, fallbackIndex: number): AtlasSousDomaine | null {
    if (!raw || typeof raw !== 'object') return null
    const row = raw as Record<string, unknown>
    const id = row.id
    if (typeof id !== 'number' && typeof id !== 'string') return null
    const fallbackName = String(row.name ?? row.nameFr ?? row.nameEn ?? row.label ?? row.code ?? `Sous-domaine ${id}`)
    const donneesInput = Array.isArray(row.donnees) ? row.donnees : []

    let donnees: AtlasDonnee[] = []
    let competenceDonnees: AtlasCompetenceDonnee[] | undefined

    if (donneesInput.length > 0 && this.isNewFormatDonnee(donneesInput[0])) {
      // New format: donnees[] = AtlasCompetenceDonnee[]
      const normalizedCompetences = donneesInput
        .map((d, i) => this.normalizeCompetenceDonnee(d, i))
        .filter((d): d is AtlasCompetenceDonnee => d != null)
      competenceDonnees = normalizedCompetences
      // Derive backward-compat AtlasDonnee[] from typeOuvrages so existing layer consumers still work.
      // A typeOuvrage can be linked to several compétences (competence_type_ouvrage is many-to-many),
      // so it shows up once per competenceDonnee here — dedupe by typId, first occurrence wins, so the
      // sidebar shows one row per type d'ouvrage instead of repeating it once per compétence.
      const seenTypIds = new Set<number>()
      donnees = normalizedCompetences
        .flatMap((cd) => cd.typeOuvrages)
        .filter((to) => {
          if (seenTypIds.has(to.typId)) return false
          seenTypIds.add(to.typId)
          return true
        })
        .map((to, i) =>
          this.normalizeDonnee(
            {
              id: to.typId,
              typeOuvrageCode: to.typeOuvrageCode,
              typeOuvrageLib: to.typeOuvrageLib,
              // nameEn de la donnée dérivée vient du type d'ouvrage : sans lui, normalizeDonnee
              // retomberait sur le libellé français et la sidebar resterait en français.
              nameEn: to.typeOuvrageLibEn ?? undefined,
              markerConfig: to.markerConfig,
              source: 'sigcom',
              count: to.count,
              hasData: to.count > 0,
              styleId: to.styleId,
            },
            i,
          ),
        )
        .filter((d): d is AtlasDonnee => d != null)
    } else {
      donnees = donneesInput
        .map((d, i) => this.normalizeDonnee(d, i))
        .filter((d): d is AtlasDonnee => d != null)
    }

    return {
      id,
      code: typeof row.code === 'string' ? row.code : undefined,
      name: typeof row.name === 'string' ? row.name : fallbackName,
      nameFr: typeof row.nameFr === 'string' ? row.nameFr : fallbackName,
      nameEn: typeof row.nameEn === 'string' ? row.nameEn : fallbackName,
      order: typeof row.order === 'number' ? row.order : (typeof row.position === 'number' ? row.position : fallbackIndex),
      donnees,
      competenceDonnees,
    }
  }

  private static normalizeCategory(raw: AtlasCategory): AtlasCategory {
    const normalizedAtlasCategory =
      typeof raw.atlas_category === 'string' && raw.atlas_category.trim().length > 0
        ? raw.atlas_category
        : raw.name

    const normalizedSousDomainesFromApi = Array.isArray(raw.sousDomaines)
      ? raw.sousDomaines
          .map((sd, i) => this.normalizeSousDomaine(sd, i))
          .filter((sd): sd is AtlasSousDomaine => sd != null)
      : []

    // Backend limitation (commune-scoped /api/atlas_categories?cmnCode=...): a sousDomaine with
    // no infrastructure inside the commune perimeter is still returned with donnees: [] instead
    // of being omitted (can't safely drop it from a Doctrine-managed collection in the API
    // hook). Filter it out here so the sidebar never shows an empty, clickable sous-domaine.
    const normalizedSousDomaines = normalizedSousDomainesFromApi.filter((sd) => sd.donnees.length > 0)

    const normalizedDonneesFromApi = Array.isArray(raw.donnees)
      ? raw.donnees
          .map((d, i) => this.normalizeDonnee(d, i))
          .filter((d): d is AtlasDonnee => d != null)
      : []

    const normalizedDonnees =
      normalizedDonneesFromApi.length > 0
        ? normalizedDonneesFromApi
        : normalizedSousDomaines.flatMap((sd) => sd.donnees)

    return {
      ...raw,
      code: typeof raw.code === 'string' ? raw.code : undefined,
      atlas_category: normalizedAtlasCategory,
      sousDomaines: normalizedSousDomaines,
      donnees: normalizedDonnees,
    }
  }

  /**
   * Fetches all atlas categories from the API.
   * Uses an in-memory cache (keyed per scope) to avoid duplicate requests.
   * @param forceRefresh Force cache invalidation before fetching
   * @param cmnCode When set, scopes the request to a commune (GET /api/atlas_categories?cmnCode=...):
   *   typeOuvrages are limited to those with infrastructures inside the commune perimeter, with a
   *   commune-scoped count. Omit for the national scope (unchanged behavior).
   */
  static async getAllCategories(forceRefresh: boolean = false, cmnCode?: string | null): Promise<AtlasCategory[]> {
    const scope = this.scopeKey(cmnCode)
    const cached = this.categoriesCacheByScope.get(scope)
    if (cached && !forceRefresh) {
      return cached
    }

    const pending = this.categoriesPromiseByScope.get(scope)
    if (pending && !forceRefresh) {
      return pending
    }

    const promise = (async () => {
      try {
        const response = await apiClient.get('/api/atlas_categories', {
          params: cmnCode ? { cmnCode } : undefined,
        })

        let categories: AtlasCategory[]
        const data = response.data
        if (typeof data === 'string' && data.trim().toLowerCase().startsWith('<!')) {
          console.warn(
            '[AtlasCategoriesService] API returned HTML (check VITE_API_URL points to the backend)',
          )
          categories = []
        } else if (data && data['hydra:member']) {
          categories = data['hydra:member'] as AtlasCategory[]
        } else if (Array.isArray(data)) {
          categories = data as AtlasCategory[]
        } else if (data && typeof data === 'object') {
          categories = [data] as AtlasCategory[]
        } else {
          console.warn('[AtlasCategoriesService] Unexpected response format')
          categories = []
        }

        const normalizedCategories = categories.map((category) => this.normalizeCategory(category))

        this.categoriesCacheByScope.set(scope, normalizedCategories)
        this.categoriesPromiseByScope.delete(scope)
        return normalizedCategories
      } catch (error) {
        this.categoriesPromiseByScope.delete(scope)
        console.error('Error fetching atlas categories:', error)
        return []
      }
    })()

    this.categoriesPromiseByScope.set(scope, promise)
    return promise
  }

  /**
   * Returns the in-memory cache synchronously, or null if not yet loaded.
   * @param cmnCode Same scope as passed to getAllCategories (omit for national).
   */
  static getCachedCategories(cmnCode?: string | null): AtlasCategory[] | null {
    return this.categoriesCacheByScope.get(this.scopeKey(cmnCode)) ?? null
  }

  /**
   * Finds the competenceId owning a typeOuvrage (by typId), across every cached scope (national
   * and any commune already fetched). Used to attach competenceId when opening the infrastructure
   * fiche from a map click, since vector tile feature properties only carry typ_id, not the parent
   * competence. The typId → competenceId mapping doesn't vary by commune, so any cached scope works.
   */
  static findCompetenceIdForTypeOuvrage(typId: number): number | undefined {
    for (const categories of this.categoriesCacheByScope.values()) {
      for (const category of categories) {
        for (const sousDomaine of category.sousDomaines ?? []) {
          for (const cd of sousDomaine.competenceDonnees ?? []) {
            if (cd.typeOuvrages.some((to) => to.typId === typId)) return cd.competenceId
          }
        }
      }
    }
    return undefined
  }

  /**
   * Finds a competenceId owning a typeOuvrage by its code, across every cached scope. Used to attach
   * competenceId when opening the infrastructure fiche from the right-panel search (only the type
   * ouvrage code is known at that point, not its typId). A typeOuvrage can belong to several
   * compétences (many-to-many); this returns the first match, which is enough to resolve a
   * reasonable attribut set for the fiche.
   */
  static findCompetenceIdForTypeOuvrageCode(typeOuvrageCode: string): number | undefined {
    const normalized = typeOuvrageCode.trim().toUpperCase()
    if (!normalized) return undefined
    for (const categories of this.categoriesCacheByScope.values()) {
      for (const category of categories) {
        for (const sousDomaine of category.sousDomaines ?? []) {
          for (const cd of sousDomaine.competenceDonnees ?? []) {
            if (cd.typeOuvrages.some((to) => to.typeOuvrageCode.trim().toUpperCase() === normalized)) {
              return cd.competenceId
            }
          }
        }
      }
    }
    return undefined
  }

  static clearCache(): void {
    this.categoriesCacheByScope.clear()
    this.categoriesPromiseByScope.clear()
  }

  /**
   * Finds the marker_config for a typeOuvrage by its vector-tile styleId (infra_type_ouvrage_{typId}
   * by default, but the backend may supply a custom styleId), across every cached scope. Used when
   * applying that style to the map so points render with the same icon configured for the type
   * d'ouvrage (and already shown in the right panel) instead of a flat colored pin.
   */
  static findMarkerConfigForTypeOuvrageStyleId(styleId: string): TypeOuvrageMarkerConfig | null {
    for (const categories of this.categoriesCacheByScope.values()) {
      for (const category of categories) {
        for (const sousDomaine of category.sousDomaines ?? []) {
          for (const cd of sousDomaine.competenceDonnees ?? []) {
            const found = cd.typeOuvrages.find((to) => to.styleId === styleId)
            if (found) return (found.markerConfig as TypeOuvrageMarkerConfig | null) ?? null
          }
        }
      }
    }
    return null
  }

  /**
   * Finds an atlas category by its name.
   * @param categoryName Name of the category to find
   */
  static async getCategoryByName(categoryName: string): Promise<AtlasCategory | null> {
    try {
      const categories = await this.getAllCategories()
      return categories.find((cat) => cat.atlas_category === categoryName || cat.name === categoryName) || null
    } catch (error) {
      console.error(`Error fetching category "${categoryName}":`, error)
      return null
    }
  }
}
